import jwt from 'jsonwebtoken';
import Cookies, { CookieSetOptions } from 'universal-cookie';
import { ACCESS_TOKEN, REDIRECT_AFTER_RESET_URL, REFRESH_TOKEN, REQUEST_URLS } from './constants';
import { getAxiosInstance, getCookieConfig, getHasRedirectAfterPasswordReset } from './setup';

const cookies = new Cookies(null, { path: '/' });

/**
 * @description This function logs in the user with email and password. It sets the access and refresh tokens in cookies and decodes the access token to get the email of the user.
 * @param email {string} The email of the user
 * @param password {string} The password of the user
 * @param setUser {Function} A function to set the user in the context
 */
export async function loginWithPassword(email: string, password: string, setUser?: (user) => void) {
  const response = await getAxiosInstance().post(REQUEST_URLS.AUTH_LOGIN_PASSWORD, {
    email,
    password,
  });

  const { accessToken, refreshToken } = response.data;
  saveTokensToCookies(accessToken, refreshToken);
  if (setUser) {
    const email = getEmailFromJwt(accessToken);
    setUser({ email });
  }
}

/**
 * @description This function sends a request to reset the password of the user.
 * @param email {string} The email of the user
 */
export async function requestPasswordReset(email: string) {
  const hasRedirect = getHasRedirectAfterPasswordReset();
  if (hasRedirect) {
    const redirectUrl = window.location.href;
    setRedirectUrlToCookies(REDIRECT_AFTER_RESET_URL, redirectUrl, getCookieConfig());
  }
  await getAxiosInstance().post(REQUEST_URLS.AUTH_FORGOT_PASSWORD, {
    email,
  });
}

/**
 * @description This function sends a request to verify the password reset code.
 * @param code {string} The password reset code
 * @returns {string} The email of the user
 */
export async function verifyPasswordResetRequest(code: string): Promise<string> {
  const response = await getAxiosInstance().post(REQUEST_URLS.AUTH_VERIFY_FORGOT_PASSWORD, {
    code,
  });
  return response.data.email;
}

/**
 * @description This function resets the password of the user and logs them in.
 * @param code {string} The password reset code
 * @param newPassword {string} The new password
 */
export async function resetPassword(code: string, newPassword: string) {
  await getAxiosInstance().post(REQUEST_URLS.AUTH_RESET_PASSWORD, {
    code,
    newPassword,
  });
}

export function redirectAfterReset() {
  const redirectUrl = getRedirectUrlFromCookies(REDIRECT_AFTER_RESET_URL);
  if (redirectUrl) {
    unsetRedirectUrlFromCookies(REDIRECT_AFTER_RESET_URL);
    window.location.replace(redirectUrl);
  }
}

/**
 * @description This function logs out the user by removing the access and refresh tokens from cookies. If a setUser function is provided, it will be called with null.
 * @param setUser
 */
export function logout(setUser?: (user) => void) {
  unsetTokensFromCookies();
  localStorage.removeItem('currentAccountId');
  localStorage.removeItem('currentUser');
  localStorage.removeItem('currentAccountUsers');
  if (setUser) setUser(null);
}

function getEmailFromJwt(accessToken: string): string {
  const { email } = jwt.decode(accessToken, { json: true }) as jwt.JwtPayload & {
    email: string;
  };
  return email;
}

function saveTokensToCookies(accessToken: string, refreshToken: string) {
  setAccessTokenToCookies(accessToken);
  setRefreshTokenToCookies(refreshToken);
}

export function getIsExpired(): boolean {
  const token = cookies.get(ACCESS_TOKEN);
  const payload = jwt.decode(token, { json: true });
  const isExpired = !payload || !payload.exp || Date.now() > payload.exp * 1000;
  return isExpired;
}

export function getAccessTokenFromCookies(): string {
  const token = cookies.get(ACCESS_TOKEN);
  return token;
}

export function getUserEmailFromCookies(): string {
  const token = getAccessTokenFromCookies();
  const payload = jwt.decode(token, { json: true });
  const email = payload?.email;
  return email;
}

export function setAccessTokenToCookies(token: string) {
  cookies.set(ACCESS_TOKEN, token, getCookieConfig());
}

export function getRefreshTokenFromCookies(): string {
  const token = cookies.get(REFRESH_TOKEN);
  return token;
}

export function setRefreshTokenToCookies(token: string) {
  cookies.set(REFRESH_TOKEN, token, getCookieConfig());
}

export function getRedirectUrlFromCookies(cookieName: string): string | undefined {
  const redirectUrl = cookies.get(cookieName);
  unsetRedirectUrlFromCookies(cookieName);
  return redirectUrl;
}

export function setRedirectUrlToCookies(
  cookieName: string,
  url: string,
  cookieOptions?: CookieSetOptions,
) {
  if (cookieOptions) {
    cookies.set(cookieName, url, cookieOptions);
  } else {
    cookies.set(cookieName, url);
  }
}

/**
 * @note You must specify cookie options in order for the cookies to go away.
 */
export function unsetTokensFromCookies(): void {
  cookies.remove(ACCESS_TOKEN, getCookieConfig());
  cookies.remove(REFRESH_TOKEN, getCookieConfig());
}

export function unsetRedirectUrlFromCookies(cookieName: string): void {
  if (cookieName === REDIRECT_AFTER_RESET_URL) {
    cookies.remove(cookieName, getCookieConfig());
  } else {
    cookies.remove(cookieName);
  }
}
